import { MutateOptions, useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { useAppState } from "../../../../../context/StateContext";
import { User } from "../../../../../models/user";
import ChecklistService from "../../../../../services/checklist.service";
import ReportsGroupsService from "../../../../../services/reports.groups.service";
import UserService from "../../../../../services/user.service";
import { Status } from "../../../../../util/enums";
import { CheckListKeys, ReportGroupKeys, UserKeys } from "../../../../../util/queries";

const CACHE_TIME = 60 * 60 * 1000 * 24; // 1 hour

export const useActionReport = () => {
  const { currentUser } = useAppState();

  const getAllReportGroup = () => {
    return useQuery({
      queryKey: ReportGroupKeys.getAll([currentUser?.company.profileId]),
      queryFn: async () => {
        const response = await ReportsGroupsService.getAll({
          companyProfileId: currentUser?.company.profileId,
          include: JSON.stringify([
            { association: "reports", include: [{ association: "reportGroup" }] },
          ]),
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.id && !!currentUser?.company.profileId,
      refetchOnWindowFocus: false,
      staleTime: 30 * 60 * 1000, // 2 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getAllCheckList = () => {
    return useQuery({
      queryKey: CheckListKeys.getAll([
        Status.ACTIVE,
        currentUser?.companyId,
        "description",
        "desc",
      ]),
      queryFn: async () =>
        ChecklistService.getAll({
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify([["description", "desc"]]),
        }),
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      staleTime: 30 * 60 * 1000, // 30 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const useUserUpdateStatus = (
    syncUser: (
      variables: void,
      options?: MutateOptions<AxiosResponse<User, any> | null, unknown, void, unknown> | undefined
    ) => void
  ) => {
    return useMutation({
      mutationKey: UserKeys.update(),
      mutationFn: ({ id, user }: { id: number; user: User }) => UserService.update(id, user),
      onSuccess: () => {
        syncUser();
      },
    });
  };

  return {
    getAllReportGroup,
    getAllCheckList,
    useUserUpdateStatus,
  };
};
